import React from 'react';
import { useParams } from 'react-router-dom';

import MDBox from 'components/MDBox';

import PayrollSettingsStrip from './settings/PayrollSettingsStrip';
import PayrollPricingRulesModal from './settings/PayrollPricingRulesModal';
import PayrollSessionsSection from './table/PayrollSessionsSection';

import { staffApi } from 'sectors/pilates/staff/api/staffApi';
import { reportsApi } from 'sectors/pilates/reports/api/reportsApi';
import { plansApi } from 'sectors/pilates/settings/plans/api/plansApi';

function getMonthRange(period) {
  if (!period || !/^\d{4}-\d{2}$/.test(period)) {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const from = `${y}-${m}-01`;
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    const to = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
    return { from, to };
  }

  const [yearStr, monthStr] = period.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);

  const from = `${yearStr}-${monthStr}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const to = `${yearStr}-${monthStr}-${String(lastDay).padStart(2, '0')}`;

  return { from, to };
}

function normalizeAttendanceStatus(v) {
  const raw = (v || '').toString().toUpperCase();

  if (['ATTENDED', 'PRESENT', 'COMPLETED'].includes(raw)) return 'ATTENDED';
  if (['NO_SHOW', 'ABSENT', 'MISSED'].includes(raw)) return 'NO_SHOW';
  if (raw === 'CANCELLED') return 'CANCELLED';
  if (['BURNED', 'BURN', 'FORFEITED'].includes(raw)) return 'BURNED';
  if (['PENDING', 'UNKNOWN', ''].includes(raw)) return 'PENDING';

  return raw || 'UNKNOWN';
}

function deriveSessionType(session) {
  const raw = session?.sessionType || session?.type || session?.lessonType || '';
  const upper = raw.toString().toUpperCase();

  if (upper.includes('GROUP')) return 'GROUP';
  if (upper.includes('PRIVATE') || upper.includes('ONE_TO_ONE')) return 'PRIVATE';

  return session?.isGroup ? 'GROUP' : 'PRIVATE';
}

function deriveMemberName(session) {
  return (
    session?.memberName ||
    session?.participantName ||
    session?.customerName ||
    session?.fullName ||
    '—'
  );
}

function toRuleKey(value) {
  return String(value || '')
    .trim()
    .toLocaleLowerCase('tr-TR');
}

function buildLessonRuleKey(participantCount) {
  return `lesson:${Number(participantCount || 1)}`;
}

function getSessionPackageLabel(session) {
  return (
    session?.packageName ||
    session?.planName ||
    session?.planSnapshot?.name ||
    session?.packagePlanNameSnapshot ||
    ''
  )
    .toString()
    .trim();
}

function getPlanCatalogName(plan) {
  return (plan?.name || plan?.planName || plan?.title || '').toString().trim();
}

function getPlanSourceLabel(plan) {
  const capacity = Number(plan?.classCapacity || 0);
  if (capacity > 0) return `Kayıtlı paket • ${capacity} kişi`;
  return 'Kayıtlı paket';
}

function buildPackageOptions(rawSessions, catalogPlans) {
  const map = new Map();

  (Array.isArray(catalogPlans) ? catalogPlans : []).forEach((plan) => {
    const label = getPlanCatalogName(plan);
    if (!label) return;

    const key = toRuleKey(label);
    if (!key || map.has(key)) return;

    map.set(key, {
      key,
      label,
      sourceLabel: getPlanSourceLabel(plan),
    });
  });

  (Array.isArray(rawSessions) ? rawSessions : []).forEach((session) => {
    const label = getSessionPackageLabel(session);
    if (!label) return;

    const key = toRuleKey(label);
    if (!key || map.has(key)) return;

    map.set(key, {
      key,
      label,
      sourceLabel: 'Seanslarda görülen paket',
    });
  });

  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'tr'));
}

function buildLessonRulesFromSessions(rawSessions, sessionParticipantCountMap, fallbackRate) {
  const counts = Array.from(
    new Set(
      (Array.isArray(rawSessions) ? rawSessions : [])
        .map((session) =>
          Number(sessionParticipantCountMap.get(String(session?.sessionId ?? session?.id)) || 0)
        )
        .filter((count) => Number.isFinite(count) && count > 0)
    )
  ).sort((a, b) => a - b);

  return counts.map((count) => ({
    key: buildLessonRuleKey(count),
    participantCount: count,
    rate: fallbackRate,
  }));
}

function buildInitialSettings(staff) {
  const payrollType = mapStaffWorkTypeToPayrollType(staff?.workType);
  const hourlyRate = staff?.hourlyRate ?? '';
  const monthlySalary = staff?.monthlySalary ?? '';
  const percentageRate = staff?.percentageRate ?? '';

  return {
    period: getCurrentPeriod(),
    payrollType,
    monthlySalary: monthlySalary === null ? '' : String(monthlySalary),
    percentageRate: percentageRate === null ? '' : String(percentageRate),
    includeMissedSessions: false,
    showUntakenAttendanceSessions: true,
    applyCardCommission: false,
    cardCommissionPercent: '',
    pricingRules: {
      lessonRates: [],
      packageRates: [],
      fallbackHourlyRate: hourlyRate === null ? '' : String(hourlyRate),
    },
  };
}

function mapStaffWorkTypeToPayrollType(workType) {
  const raw = String(workType || '').toUpperCase();

  if (['FULL_TIME', 'MONTHLY'].includes(raw)) return 'MONTHLY';
  if (['PERCENTAGE', 'COMMISSION'].includes(raw)) return 'PERCENTAGE';
  return 'HOURLY';
}

function getCurrentPeriod() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function parseConfiguredRate(value) {
  if (value === '' || value === null || value === undefined) return null;
  const rate = Number(value);
  return Number.isFinite(rate) && rate >= 0 ? rate : null;
}

function getPackageRate(session, settings) {
  const packageLabel = getSessionPackageLabel(session);
  if (!packageLabel) return { match: null, warning: '' };

  const key = toRuleKey(packageLabel);
  const packageRules = Array.isArray(settings?.pricingRules?.packageRates)
    ? settings.pricingRules.packageRates
    : [];

  const matched = packageRules.find((item) => toRuleKey(item?.key || item?.label) === key);
  if (!matched) return { match: null, warning: '' };

  const rate = parseConfiguredRate(matched.rate);
  if (rate === null) {
    return {
      match: null,
      warning: `${packageLabel} paketi için ücret tanımı eksik.`,
    };
  }

  return {
    match: {
      amount: rate,
      key,
      label: matched.label || packageLabel,
      type: 'PACKAGE_RATE',
      description: `${matched.label || packageLabel} paketi`,
    },
    warning: '',
  };
}

function getLessonRate(sessionCount, settings, sessionType) {
  const lessonRules = Array.isArray(settings?.pricingRules?.lessonRates)
    ? settings.pricingRules.lessonRates
    : [];

  const matched = lessonRules.find(
    (item) => Number(item?.participantCount || 0) === Number(sessionCount || 0)
  );
  if (matched) {
    const rate = parseConfiguredRate(matched.rate);
    if (rate !== null) {
      return {
        match: {
          amount: rate,
          key: matched.key,
          label: `${Number(sessionCount || 0)} kişilik ders`,
          type: 'LESSON_RATE',
          description: `${Number(sessionCount || 0)} kişilik ders`,
        },
        warning: '',
      };
    }

    return {
      match: null,
      warning: `${Number(sessionCount || 0)} kişilik ders için ücret tanımı eksik.`,
    };
  }

  const fallback = parseConfiguredRate(settings?.pricingRules?.fallbackHourlyRate);
  if (fallback !== null && fallback > 0) {
    return {
      match: {
        amount: fallback,
        key: 'fallback-hourly',
        label: 'Varsayılan saatlik ücret',
        type: 'FALLBACK_RATE',
        description: 'Varsayılan saatlik ücret',
      },
      warning: '',
    };
  }

  if (sessionType === 'GROUP') {
    const legacyGroupRate = parseConfiguredRate(settings?.groupLessonHourlyRate);
    if (legacyGroupRate !== null) {
      return {
        match: {
          amount: legacyGroupRate,
          key: 'legacy-group-hourly',
          label: 'Eski grup saatlik ücreti',
          type: 'LEGACY_GROUP_RATE',
          description: 'Eski grup saatlik ücreti',
        },
        warning: '',
      };
    }
  }

  const legacyPrivateRate = parseConfiguredRate(settings?.privateLessonHourlyRate);
  if (legacyPrivateRate !== null) {
    return {
      match: {
        amount: legacyPrivateRate,
        key: 'legacy-private-hourly',
        label: 'Eski özel ders ücreti',
        type: 'LEGACY_PRIVATE_RATE',
        description: 'Eski özel ders ücreti',
      },
      warning: '',
    };
  }

  return {
    match: null,
    warning: `${Number(sessionCount || 0)} kişilik ders için kullanılabilir ücret bulunamadı.`,
  };
}

function resolveHourlyPricing(session, settings, sessionCount, sessionType) {
  const lessonResult = getLessonRate(sessionCount, settings, sessionType);
  const packageResult = getPackageRate(session, settings);
  const warnings = [packageResult.warning, lessonResult.warning].filter(Boolean);
  const appliedMatch = packageResult.match || lessonResult.match || null;

  if (!appliedMatch) {
    return {
      amount: 0,
      appliedPricingType: 'MISSING_RATE',
      appliedPricingLabel: 'Ücret kuralı eksik',
      pricingWarningText: warnings[0] || 'Bu seans için uygulanacak ücret bulunamadı.',
      pricingRuleKey: '',
      pricingStrategyLabel: 'Kişi sayısı bazlı, paket override',
    };
  }

  return {
    amount: appliedMatch.amount,
    appliedPricingType: appliedMatch.type,
    appliedPricingLabel: appliedMatch.description,
    pricingWarningText: warnings.find(Boolean) || '',
    pricingRuleKey: appliedMatch.key,
    pricingStrategyLabel: packageResult.match
      ? 'Paket fiyatı seans ücretini ezdi'
      : 'Kişi sayısına göre seans ücreti',
  };
}

function derivePayrollAmount(session, settings, sessionCount) {
  const sessionType = deriveSessionType(session);
  const attendanceStatus = normalizeAttendanceStatus(session?.attendanceStatus || session?.status);

  const isIncludedStatus =
    attendanceStatus === 'ATTENDED' ||
    (Boolean(settings.includeMissedSessions) &&
      ['NO_SHOW', 'CANCELLED', 'BURNED'].includes(attendanceStatus));

  if (!isIncludedStatus) {
    return {
      amount: 0,
      appliedPricingType: 'EXCLUDED_STATUS',
      appliedPricingLabel: 'Bordroya dahil edilmeyen durum',
      pricingWarningText: '',
      pricingRuleKey: '',
    };
  }

  if (settings.payrollType === 'MONTHLY') {
    return {
      amount: 0,
      appliedPricingType: 'MONTHLY',
      appliedPricingLabel: 'Aylık maaş',
      pricingWarningText: '',
      pricingRuleKey: '',
    };
  }

  if (settings.payrollType === 'PERCENTAGE') {
    const percentage = Number(settings.percentageRate || 0);
    const baseAmount =
      Number(session?.paymentAmountTry) ||
      Number(session?.collectedAmountTry) ||
      Number(session?.priceTry) ||
      Number(session?.amountTry) ||
      0;

    if (!baseAmount || !percentage) {
      return {
        amount: 0,
        appliedPricingType: 'MISSING_PERCENTAGE_RATE',
        appliedPricingLabel: 'Yüzdelik kural eksik',
        pricingWarningText: 'Yüzdelik hesap için oran veya tahsilat tutarı eksik.',
        pricingRuleKey: '',
      };
    }

    return {
      amount: Math.round((baseAmount * percentage) / 100),
      appliedPricingType: 'PERCENTAGE_RATE',
      appliedPricingLabel: `%${percentage} tahsilat oranı`,
      pricingWarningText: '',
      pricingRuleKey: 'percentage-rate',
    };
  }

  return resolveHourlyPricing(session, settings, sessionCount, sessionType);
}

function mapSessionToPayrollRow(session, settings, sessionCount) {
  const attendanceStatus = normalizeAttendanceStatus(session?.attendanceStatus || session?.status);
  const pricing = derivePayrollAmount(session, settings, sessionCount);
  const payrollAmountTry = pricing?.amount || 0;

  const includedInPayroll =
    attendanceStatus === 'ATTENDED' ||
    (Boolean(settings.includeMissedSessions) &&
      ['NO_SHOW', 'CANCELLED', 'BURNED'].includes(attendanceStatus));

  let warningText = '';

  if (attendanceStatus === 'PENDING') {
    warningText = 'Bu seans için henüz yoklama alınmamış.';
  } else if (
    ['NO_SHOW', 'CANCELLED', 'BURNED'].includes(attendanceStatus) &&
    !settings.includeMissedSessions
  ) {
    warningText = 'Katılım olmadığı için bordroya dahil edilmedi.';
  }

  return {
    participantId: session?.participantId ?? session?.memberId ?? session?.id,
    sessionId: session?.sessionId ?? session?.id,
    memberId: session?.memberId ?? null,
    memberPlanId: session?.memberPlanId ?? null,

    startAt: session?.startAt ?? session?.sessionStartAt ?? null,
    dayISO: session?.dayISO ?? '',
    startMin: session?.startMin ?? null,
    durationMin: session?.durationMin ?? null,

    activityName: session?.activityName || '—',
    memberName: deriveMemberName(session),

    sessionType: session?.sessionType || '',
    billingMode: session?.billingMode || '',
    sessionParticipantCount: sessionCount,

    attendanceStatus,
    attendanceTakenAt: session?.attendanceTakenAt ?? null,
    attendanceTakenByName: session?.attendanceTakenByName || '',
    attendanceTakenByUserId: session?.attendanceTakenByUserId ?? null,

    rsvpStatus: session?.rsvpStatus || '',
    rsvpReportedAt: session?.rsvpReportedAt ?? null,

    isGroup: Boolean(session?.isGroup),

    packageName: session?.packageName || '',
    packagePeriod: session?.packagePeriod || '',
    packageTotalSession: session?.packageTotalSession ?? null,
    packageUsedSessionIndex: session?.packageUsedSessionIndex ?? null,

    planDebtTry: Number(session?.planDebtTry || 0),
    planPaidTry: Number(session?.planPaidTry || 0),
    planPriceTry: Number(session?.planPriceTry || 0),
    planPayStatus: session?.planPayStatus || 'UNKNOWN',

    paymentMethod: session?.paymentMethod || session?.lastPaymentMethod || '',
    lastPaymentMethod: session?.lastPaymentMethod || '',

    autoPayrollAmountTry: payrollAmountTry,
    payrollAmountTry,
    includedInPayroll,
    warningText,
    pricingWarningText: pricing?.pricingWarningText || '',
    appliedPricingType: pricing?.appliedPricingType || 'UNKNOWN',
    appliedPricingLabel: pricing?.appliedPricingLabel || 'Bilinmeyen kural',
    pricingRuleKey: pricing?.pricingRuleKey || '',
    pricingStrategyLabel: pricing?.pricingStrategyLabel || '',
    note: session?.note || '',

    trainerId: session?.trainerId ?? null,
    trainerName: session?.trainerName || '',
  };
}

function shouldShowRow(row, settings) {
  const attendanceStatus = normalizeAttendanceStatus(row?.attendanceStatus);

  if (attendanceStatus === 'PENDING') {
    return Boolean(settings?.showUntakenAttendanceSessions);
  }

  if (['NO_SHOW', 'CANCELLED', 'BURNED'].includes(attendanceStatus)) {
    return Boolean(settings?.includeMissedSessions);
  }

  return true;
}

export default function PayrollCalculatePage() {
  const { staffId } = useParams();

  const [settings, setSettings] = React.useState(() => buildInitialSettings(null));
  const [staff, setStaff] = React.useState(null);
  const [catalogPlans, setCatalogPlans] = React.useState([]);
  const [rawSessions, setRawSessions] = React.useState([]);
  const [sessionOverrides, setSessionOverrides] = React.useState({});
  const [loadingStaff, setLoadingStaff] = React.useState(false);
  const [loadingSessions, setLoadingSessions] = React.useState(false);
  const [error, setError] = React.useState('');
  const [pricingModalOpen, setPricingModalOpen] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    async function loadStaff() {
      if (!staffId) return;

      try {
        setLoadingStaff(true);
        setError('');

        const allStaff = await staffApi.list();
        if (!active) return;

        const found = allStaff.find((item) => String(item.id) === String(staffId));
        setStaff(found || null);
        if (found) {
          setSettings((prev) => ({
            ...buildInitialSettings(found),
            includeMissedSessions: prev.includeMissedSessions,
            showUntakenAttendanceSessions: prev.showUntakenAttendanceSessions,
            applyCardCommission: prev.applyCardCommission,
            cardCommissionPercent: prev.cardCommissionPercent,
            pricingRules: {
              ...buildInitialSettings(found).pricingRules,
              ...(prev.pricingRules || {}),
            },
            period: prev.period || getCurrentPeriod(),
          }));
        }
      } catch (err) {
        console.error('Staff bilgisi alınamadı:', err);
        if (!active) return;
        setError('Personel bilgisi alınamadı.');
      } finally {
        if (active) setLoadingStaff(false);
      }
    }

    loadStaff();

    return () => {
      active = false;
    };
  }, [staffId]);

  React.useEffect(() => {
    let active = true;

    async function loadPlans() {
      try {
        const res = await plansApi.list();
        if (!active) return;
        setCatalogPlans(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error('Plan listesi alınamadı:', err);
        if (!active) return;
        setCatalogPlans([]);
      }
    }

    loadPlans();
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    let active = true;

    async function loadSessions() {
      if (!staffId) {
        setRawSessions([]);
        setSessionOverrides({});
        return;
      }

      try {
        setLoadingSessions(true);
        setError('');

        const { from, to } = getMonthRange(settings.period);

        const sessions = await reportsApi.listTrainerSessions({
          trainerId: Number(staffId),
          from,
          to,
          limit: 500,
        });

        if (!active) return;
        setRawSessions(Array.isArray(sessions) ? sessions : []);
        setSessionOverrides({});
      } catch (err) {
        console.error('Session listesi alınamadı:', err);
        if (!active) return;
        setRawSessions([]);
        setSessionOverrides({});
        setError('Eğitmen seansları alınamadı.');
      } finally {
        if (active) setLoadingSessions(false);
      }
    }

    loadSessions();

    return () => {
      active = false;
    };
  }, [staffId, settings.period]);

  const sessionParticipantCountMap = React.useMemo(() => {
    const map = new Map();

    (Array.isArray(rawSessions) ? rawSessions : []).forEach((session) => {
      const key = String(session?.sessionId ?? session?.id ?? '');
      if (!key) return;
      map.set(key, Number(map.get(key) || 0) + 1);
    });

    return map;
  }, [rawSessions]);

  const packageOptions = React.useMemo(
    () => buildPackageOptions(rawSessions, catalogPlans),
    [rawSessions, catalogPlans]
  );

  const lessonRules = React.useMemo(() => {
    const existing = Array.isArray(settings?.pricingRules?.lessonRates)
      ? settings.pricingRules.lessonRates
      : [];

    return existing
      .slice()
      .sort((a, b) => Number(a.participantCount || 0) - Number(b.participantCount || 0));
  }, [settings]);

  React.useEffect(() => {
    const fallbackRate = settings?.pricingRules?.fallbackHourlyRate ?? '';
    const autoRules = buildLessonRulesFromSessions(
      rawSessions,
      sessionParticipantCountMap,
      fallbackRate
    );

    setSettings((prev) => {
      const prevRules = Array.isArray(prev?.pricingRules?.lessonRates)
        ? prev.pricingRules.lessonRates
        : [];

      const merged = autoRules.map((autoRule) => {
        const existing = prevRules.find((item) => item.key === autoRule.key);
        return existing ? { ...autoRule, rate: existing.rate } : autoRule;
      });

      const same =
        merged.length === prevRules.length &&
        merged.every(
          (rule, index) =>
            prevRules[index]?.key === rule.key &&
            prevRules[index]?.rate === rule.rate &&
            prevRules[index]?.participantCount === rule.participantCount
        );

      if (same) return prev;

      return {
        ...prev,
        pricingRules: {
          ...(prev.pricingRules || {}),
          lessonRates: merged,
        },
      };
    });
  }, [rawSessions, sessionParticipantCountMap, settings?.pricingRules?.fallbackHourlyRate]);

  const payrollRows = React.useMemo(
    () =>
      (Array.isArray(rawSessions) ? rawSessions : [])
        .map((session) => {
          const sessionKey = String(session?.sessionId ?? session?.id ?? '');
          const sessionCount = Number(sessionParticipantCountMap.get(sessionKey) || 1);
          const baseRow = mapSessionToPayrollRow(session, settings, sessionCount);
          const override = sessionOverrides[String(baseRow.sessionId)] || {};
          const hasManualPayrollOverride =
            override.manualPayrollAmountTry !== undefined &&
            override.manualPayrollAmountTry !== null;

          return {
            ...baseRow,
            includedInPayroll:
              typeof override.includedInPayroll === 'boolean'
                ? override.includedInPayroll
                : baseRow.includedInPayroll,
            payrollAmountTry: hasManualPayrollOverride
              ? override.manualPayrollAmountTry
              : baseRow.autoPayrollAmountTry,
            hasManualPayrollOverride,
          };
        })
        .filter((row) => shouldShowRow(row, settings)),
    [rawSessions, sessionParticipantCountMap, settings, sessionOverrides]
  );

  const handleToggleIncluded = React.useCallback((sessionRow, checked) => {
    setSessionOverrides((prev) => ({
      ...prev,
      [String(sessionRow.sessionId)]: {
        ...(prev[String(sessionRow.sessionId)] || {}),
        includedInPayroll: checked,
      },
    }));
  }, []);

  const handleChangePayrollAmount = React.useCallback((sessionRow, value) => {
    setSessionOverrides((prev) => {
      const key = String(sessionRow.sessionId);
      const current = prev[key] || {};

      if (value === '') {
        const nextEntry = { ...current };
        delete nextEntry.manualPayrollAmountTry;
        return {
          ...prev,
          [key]: nextEntry,
        };
      }

      return {
        ...prev,
        [key]: {
          ...current,
          manualPayrollAmountTry: Number(value),
        },
      };
    });
  }, []);

  const handleLessonRateChange = React.useCallback((ruleKey, value) => {
    setSettings((prev) => ({
      ...prev,
      pricingRules: {
        ...(prev.pricingRules || {}),
        lessonRates: (prev?.pricingRules?.lessonRates || []).map((item) =>
          item.key === ruleKey ? { ...item, rate: value } : item
        ),
      },
    }));
  }, []);

  const handlePackageRateChange = React.useCallback((ruleKey, value) => {
    setSettings((prev) => ({
      ...prev,
      pricingRules: {
        ...(prev.pricingRules || {}),
        packageRates: (prev?.pricingRules?.packageRates || []).map((item) =>
          item.key === ruleKey ? { ...item, rate: value } : item
        ),
      },
    }));
  }, []);

  const handleAddPackageRule = React.useCallback((option) => {
    setSettings((prev) => {
      const existing = Array.isArray(prev?.pricingRules?.packageRates)
        ? prev.pricingRules.packageRates
        : [];

      if (existing.some((item) => toRuleKey(item.key) === toRuleKey(option.key))) return prev;

      return {
        ...prev,
        pricingRules: {
          ...(prev.pricingRules || {}),
          packageRates: [
            ...existing,
            {
              key: option.key,
              label: option.label,
              sourceLabel: option.sourceLabel,
              rate: '',
            },
          ],
        },
      };
    });
  }, []);

  const handleRemovePackageRule = React.useCallback((ruleKey) => {
    setSettings((prev) => ({
      ...prev,
      pricingRules: {
        ...(prev.pricingRules || {}),
        packageRates: (prev?.pricingRules?.packageRates || []).filter(
          (item) => item.key !== ruleKey
        ),
      },
    }));
  }, []);

  const handleExportPdf = React.useCallback(() => {
    console.log('PDF export tetiklendi');
    console.log('staffId:', staffId);
    console.log('staff:', staff);
    console.log('settings:', settings);
    console.log('rows:', payrollRows);
  }, [staffId, staff, settings, payrollRows]);

  return (
    <MDBox sx={{ display: 'grid', gap: 1.25 }}>
      <PayrollSettingsStrip
        staff={staff}
        value={settings}
        onChange={(patch) => setSettings((prev) => ({ ...prev, ...patch }))}
        onOpenPricingModal={() => setPricingModalOpen(true)}
        lessonRuleCount={lessonRules.length}
        packageRuleCount={(settings?.pricingRules?.packageRates || []).length}
      />

      <PayrollSessionsSection
        rows={payrollRows}
        includeMissedSessions={Boolean(settings.includeMissedSessions)}
        applyCardCommission={Boolean(settings.applyCardCommission)}
        cardCommissionPercent={Number(settings.cardCommissionPercent || 0)}
        payrollType={settings.payrollType}
        monthlySalary={Number(settings.monthlySalary || 0)}
        onToggleIncluded={handleToggleIncluded}
        onChangePayrollAmount={handleChangePayrollAmount}
        onExportPdf={handleExportPdf}
        loading={loadingStaff || loadingSessions}
        error={error}
        rawCount={rawSessions.length}
      />

      <PayrollPricingRulesModal
        open={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
        lessonRules={lessonRules}
        packageRules={settings?.pricingRules?.packageRates || []}
        packageOptions={packageOptions.filter(
          (option) =>
            !(settings?.pricingRules?.packageRates || []).some(
              (rule) => toRuleKey(rule.key) === toRuleKey(option.key)
            )
        )}
        onLessonRateChange={handleLessonRateChange}
        onPackageRateChange={handlePackageRateChange}
        onAddPackageRule={handleAddPackageRule}
        onRemovePackageRule={handleRemovePackageRule}
      />
    </MDBox>
  );
}
